import type {
    AdvancedFilterModel,
    DateAdvancedFilterModel,
    DateFilterModel,
    FilterModel,
    NumberFilterModel,
    SetFilterModel,
    TextFilterModel,
} from '@ag-grid-community/core';
import type { ServerSideVariables } from './ag-datasource';

type DataSourceMapValue = string | number | DataSourceMap | string[] | boolean;
type DataSourceMap = Map<string, DataSourceMapValue>;

export type FilterModels = FilterModel | AdvancedFilterModel;

/**
 * Map an AG-Grid filter type to the corresponding GraphQL operator
 */
const GraphQlOperatorMap = new Map<string, string>([
    // for equals, we don't need to do anything, only pass the field name itself, e.g { id: "ABC" }
    ['equals', ''],
    ['notEqual', 'NEQ'],
    ['contains', 'ContainsFold'],
    ['greaterThan', 'GT'],
    ['greaterThanOrEqual', 'GTE'],
    ['lessThan', 'LT'],
    ['lessThanOrEqual', 'LTE'],
]);

function setFilterQuery(
    colId: string,
    model: FilterModels,
    storedFilterModel: DataSourceMap,
): DataSourceMap {
    if ('filterType' in model) {
        // TODO: support filterType 'set', which currently not being used anywhere but might be used in the future
        switch (model.filterType) {
            case 'text': {
                setTextValue(colId, model, storedFilterModel);
                break;
            }
            case 'number': {
                setNumberValue(colId, model, storedFilterModel);
                break;
            }
            case 'date': {
                setDateValue(colId, model, storedFilterModel);
                break;
            }
            case 'set': {
                setSetValue(colId, model, storedFilterModel);
                break;
            }
            default: {
                console.warn(
                    `Could not convert AG-Grid filterType: "${model.filterType ?? 'undefined'}" for column "${colId}" to a GraphQL where statement.`,
                );
                break;
            }
        }
    }
    return storedFilterModel;
}

/**
 * Convert a filter model to a GraphQL where statement
 *
 * @privateRemarks
 *
 * This function is covered by unit tests in __tests__/utils/ag-datasource.test.ts
 */
export function convertFilterModelToGraphQLWhereStatement(
    model: FilterModels,
): DataSourceMap {
    const initialMap = new Map<string, DataSourceMapValue>();

    return Object.entries(model).reduce((accum, [colId, filterModel]) => {
        /**
         * If the column ID contains a dot, it means the column represents a nested field from data, e.g. "vendor.name".
         * In this case, we need to create a nested map to store the filter query.
         */
        if (colId.includes('.')) {
            const [parent, child] = colId.split('.');
            const capitalizedParent = parent[0].toUpperCase() + parent.slice(1);
            const queryName = `has${capitalizedParent}With`;
            let nestedMap = accum.get(queryName) as DataSourceMap | undefined;
            if (!nestedMap) {
                nestedMap = new Map();
                accum.set(queryName, nestedMap);
            }
            setFilterQuery(child, filterModel as FilterModel, nestedMap);
        } else {
            setFilterQuery(colId, filterModel as FilterModel, accum);
        }
        return accum;
    }, initialMap);
}

/**
 * Get the GraphQL operator for a given filter type
 * @param type - The filter type
 * @returns The GraphQL operator or an empty string if no operator is found
 */
function getGraphQlOperator(type?: string | null): string {
    if (!type) return '';
    return GraphQlOperatorMap.get(type) ?? '';
}

function isTextValueBoolean(value: string): boolean {
    return value === 'true' || value === 'false';
}

function convertTextValueToBoolean(value: string): boolean {
    if (value === 'true') {
        return true;
    } else if (value === 'false') {
        return false;
    }

    throw new Error(`Unable to convert string "${value}" to an boolean`);
}

function setTextValue<T extends DataSourceMap>(
    colId: string,
    model: TextFilterModel,
    record: T,
): void {
    if (!model.filter) {
        return;
    }

    const operator = getGraphQlOperator(model.type);
    // Convert a string value of "true" or "false" to an actual boolean,
    // but only in cases where the filter is configured to be of type "equals".
    // This is to ensure that filtering a column of "true" or "false" values works as intended.
    const value =
        isTextValueBoolean(model.filter) && model.type === 'equals'
            ? convertTextValueToBoolean(model.filter)
            : model.filter;
    record.set(`${colId}${operator}`, value);
}

function setNumberValue<T extends DataSourceMap>(
    colId: string,
    model: NumberFilterModel,
    record: T,
): void {
    if (model.filter === null || model.filter === undefined) {
        return;
    }
    // Handle inRange filter a bit differently
    if (model.type === 'inRange' && model.filter && model.filterTo) {
        record.set(`${colId}GTE`, model.filter);
        record.set(`${colId}LTE`, model.filterTo);
        return;
    }
    const operator = getGraphQlOperator(model.type);
    record.set(`${colId}${operator}`, model.filter);
}

function setDateValue<T extends DataSourceMap>(
    colId: string,
    model: DateFilterModel | DateAdvancedFilterModel,
    record: T,
): void {
    if (
        model.type === 'inRange' &&
        'dateFrom' in model &&
        'dateTo' in model &&
        model.dateFrom !== null &&
        model.dateTo !== null
    ) {
        const dateFrom = new Date(model.dateFrom).toString();
        const dateTo = new Date(model.dateTo).toString();
        record.set(`${colId}GTE`, dateFrom);
        record.set(`${colId}LTE`, dateTo);
        return;
    }

    if ('dateFrom' in model && model.dateFrom !== null) {
        const operator = getGraphQlOperator(model.type);
        const date = new Date(model.dateFrom);
        record.set(`${colId}${operator}`, date.toISOString());
    }
}

function setSetValue<T extends DataSourceMap>(
    colId: string,
    model: SetFilterModel,
    record: T,
): void {
    if (!Array.isArray(model.values)) return;
    if (model.values.length === 1) {
        const value = model.values[0];
        if (!value) return;
        const booleanOrFallback = isTextValueBoolean(value)
            ? convertTextValueToBoolean(value)
            : value;
        record.set(colId, booleanOrFallback);
    } else if (model.values.length > 1) {
        record.set(`${colId}In`, model.values as string[]);
    }
}

export function mapToObjectRec(map: DataSourceMap): ServerSideVariables {
    const object: ServerSideVariables = {};
    for (const [key, value] of map) {
        if (value instanceof Map) {
            object[key] = mapToObjectRec(value);
        } else {
            object[key] = value;
        }
    }
    return object;
}
